import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconAlertTriangle, IconEye, IconEyeOff, IconGripVertical, IconLock, IconLockOpen, IconMovie, IconPhoto, IconTypography } from '@tabler/icons-react';
import { useArtEditorContext } from '../../../context';
import { iconComponent } from '../../../../model/icons';
import { overflowingLayers } from '../../../../model/geometry';
import { ShapePreview } from './ElementsPanel';
import { gsap, reducedMotion } from '../../../motion';

export function layerName(layer, t) {
  if (layer.name) return layer.name;
  if (layer.type === 'text') return layer.text.split('\n')[0].trim().slice(0, 48) || t('layerText');
  if (layer.binding) return t(`binding_${layer.binding}`);
  return t(`layer_${layer.type}`);
}

function Thumb({ layer }) {
  if (layer.type === 'text') return <IconTypography aria-hidden="true" />;
  if (layer.type === 'image') return layer.source?.url ? <img src={layer.source.url} alt="" draggable={false} /> : <IconPhoto aria-hidden="true" />;
  if (layer.type === 'video') return <IconMovie aria-hidden="true" />;
  if (layer.type === 'icon') { const Icon = iconComponent(layer.icon); return <Icon aria-hidden="true" />; }
  if (layer.fill?.type === 'image' && layer.fill.source?.url) return <img src={layer.fill.source.url} alt="" draggable={false} />;
  return <ShapePreview shape={layer.shape} />;
}

// The page's layers, front first. Rows drag by their grip to reorder (the others make
// room as it passes them, GSAP), or move with Alt + ↑/↓; each can be hidden, locked and
// renamed (double-click the name). Text that runs off the art is flagged.
export default function LayersPanel() {
  const { t } = useTranslation('artEditor');
  const { editor, readOnly, openLayerMenu } = useArtEditorContext();
  const list = useRef(null);
  const drag = useRef(null);
  const [renaming, setRenaming] = useState(null);
  const page = editor.page;
  if (!page) return null;
  const layers = page.layers.slice().reverse();
  const last = page.layers.length - 1;
  const overflow = new Set(overflowingLayers(page, editor.document).map(layer => layer.id));
  const moveTo = (layer, visualIndex) => editor.reorder(layer.id, last - visualIndex);

  const onGripDown = (event, index) => {
    if (readOnly || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const rows = [...list.current.children];
    const step = rows.length > 1 ? rows[1].getBoundingClientRect().top - rows[0].getBoundingClientRect().top : rows[0].getBoundingClientRect().height;
    drag.current = { index, to: index, start: event.clientY, rows, step, layer: layers[index] };
    rows[index].classList.add('is-dragging');
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onGripMove = event => {
    const current = drag.current;
    if (!current) return;
    const dy = event.clientY - current.start;
    const to = Math.max(0, Math.min(current.rows.length - 1, current.index + Math.round(dy / current.step)));
    gsap.set(current.rows[current.index], { y: dy });
    if (to !== current.to) {
      current.to = to;
      current.rows.forEach((row, index) => {
        if (index === current.index) return;
        const shift = current.index < index && index <= to ? -current.step : to <= index && index < current.index ? current.step : 0;
        gsap.to(row, { y: shift, duration: reducedMotion() ? 0 : 0.16, ease: 'power2.out', overwrite: 'auto' });
      });
    }
  };
  const onGripUp = event => {
    const current = drag.current;
    drag.current = null;
    if (!current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    gsap.killTweensOf(current.rows);
    gsap.set(current.rows, { clearProps: 'transform' });
    current.rows[current.index].classList.remove('is-dragging');
    if (current.to !== current.index) moveTo(current.layer, current.to);
  };

  return <>
    <Text.caption contrast={6}>{t('layersHint')}</Text.caption>
    <ol ref={list} className="art-layers" aria-label={t('toolLayers')}>
      {layers.map((layer, index) => {
        const active = editor.selection.includes(layer.id);
        const name = layerName(layer, t);
        return <li key={layer.id} className={`art-layer ${active ? 'is-active' : ''} ${layer.visible ? '' : 'is-hidden'}`} onContextMenu={event => openLayerMenu?.(layer, event)}>
          <span className="art-layer__grip" role="presentation" onPointerDown={event => onGripDown(event, index)} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}>
            <IconGripVertical aria-hidden="true" />
          </span>
          <button type="button" className="art-layer__main" aria-pressed={active} aria-label={`${name}${layer.locked ? ` (${t('locked')})` : ''}`}
            onClick={() => editor.select([layer.id])}
            onDoubleClick={() => !readOnly && setRenaming(layer.id)}
            onKeyDown={event => {
              if (!event.altKey || readOnly || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              event.stopPropagation();
              const target = Math.max(0, Math.min(layers.length - 1, index + (event.key === 'ArrowUp' ? -1 : 1)));
              if (target !== index) moveTo(layer, target);
            }}>
            <span className="art-layer__thumb"><Thumb layer={layer} /></span>
            {renaming === layer.id
              ? <input className="art-layer__rename" autoFocus defaultValue={layer.name || name} aria-label={t('renameLayer')} maxLength={60}
                onClick={event => event.stopPropagation()}
                onKeyDown={event => { event.stopPropagation(); if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') setRenaming(null); }}
                onBlur={event => { const value = event.target.value.trim(); setRenaming(null); if (value !== (layer.name || name)) editor.update(layer.id, { name: value || null }, { key: null }); }} />
              : <Text.span contrast={layer.visible ? 9 : 5} className="art-layer__name">{name}</Text.span>}
            {overflow.has(layer.id) && <span className="art-layer__warning" title={t('overflowWarning')}><IconAlertTriangle aria-label={t('overflowWarning')} /></span>}
          </button>
          <Button size="micro" variant="text" color="overlay" preventLoading disabled={readOnly} icon={layer.visible ? <IconEye /> : <IconEyeOff />}
            aria-label={t(layer.visible ? 'hideLayer' : 'showLayer')} tooltip={t(layer.visible ? 'hideLayer' : 'showLayer')}
            onClick={() => editor.update(layer.id, { visible: !layer.visible }, { key: null })} />
          <Button size="micro" variant="text" color="overlay" preventLoading disabled={readOnly} icon={layer.locked ? <IconLock /> : <IconLockOpen />}
            className={layer.locked ? 'is-active' : ''} aria-label={t(layer.locked ? 'unlockLayer' : 'lockLayer')} tooltip={t(layer.locked ? 'unlockLayer' : 'lockLayer')}
            onClick={() => editor.update(layer.id, { locked: !layer.locked }, { key: null })} />
        </li>;
      })}
    </ol>
    {!layers.length && <Text.caption contrast={6}>{t('layersEmpty')}</Text.caption>}
  </>;
}
